/**
 * @fileoverview Single source of truth for "can this inspection be completed?".
 * @module core-domain/inspection
 *
 * Responsibilities:
 * - The ONE canonical process that decides whether an inspection may be
 *   completed. Consumed by tenant-web (office conductor), mobile-pwa (field
 *   completion), and mirrored server-side so completion is enforced
 *   authoritatively, not just as button UX.
 * - Aggregates every per-widget completion requirement the inspection template
 *   already declares: required-field-answered, format/length/range, engine-v2
 *   require-notes/photos, and the failing-question -> occurrence(+location/
 *   photo/notes/corrective) rule.
 *
 * Dependencies:
 * - `./types`, `./rulesEngine` (getFailingMappings), `./passFailState`
 *   (isPassFailFailing), `./fieldValidation` (getFieldIssues).
 *
 * Architecture:
 * - Pure functions. No React, no Dexie, no Mongo, no fetch. This is the single
 *   place to add or change a completion requirement; both hosts and the server
 *   mirror delegate here and never re-implement a rule inline (DRY).
 *
 * Notes:
 * - Completion exceptions are authoritative: a widget on a hidden form, in a
 *   hidden section, a hidden widget, or an N/A widget produces NO issue.
 * - ANY blocking issue means the inspection cannot be completed. There is no
 *   soft/advisory path for these rules.
 */
import type {
  InspectionForm,
  InspectionFormField,
  FieldResponse,
  ResolvedFormStateV2,
} from './types';
import { getFailingMappings } from './rulesEngine';
import { isPassFailField, isPassFailFailing, getPassFailExtras } from './passFailState';
import {
  getFieldIssues,
  type FieldIssue,
  type FieldOccurrenceInput,
  type FieldViolationContext,
} from './fieldValidation';

/**
 * @contract CompletionFormInput
 * Purpose: one form's worth of data the validator needs, supplied by the host
 * from its local-first (Dexie) state — no IO happens inside the validator.
 * Fields:
 * - form: canonical InspectionForm definition (fields, layout, formKind).
 * - responses: fieldId -> persisted FieldResponse (value, isNA).
 * - resolved: engine-v2 resolved state (form/section/field hidden, required).
 * - occurrencesByField: fieldId -> logged violation occurrences for that field.
 * Rules: occurrencesByField only needs entries for failing fields.
 */
export interface CompletionFormInput {
  form: InspectionForm;
  responses: Record<string, FieldResponse>;
  resolved?: ResolvedFormStateV2;
  occurrencesByField?: Record<string, FieldOccurrenceInput[]>;
}

/**
 * @contract CompletionValidationInput
 * Purpose: the full set of forms being completed in one inspection.
 * Fields: forms — every form the host is about to finalize.
 * Rules: plain data both hosts already hold; no host-specific types.
 */
export interface CompletionValidationInput {
  forms: CompletionFormInput[];
}

/**
 * @contract CompletionVerdict
 * Purpose: the canonical answer to "can complete?".
 * Fields:
 * - canComplete: true iff there are zero blocking issues.
 * - blocking: every issue that prevents completion (hard block).
 * - advisory: reserved for non-blocking notices (always empty today).
 * Rules: hosts gate the Complete button on `canComplete`; the server rejects a
 *   completion mutation when `canComplete === false`.
 */
export interface CompletionVerdict {
  canComplete: boolean;
  blocking: FieldIssue[];
  advisory: FieldIssue[];
}

/**
 * @process buildFieldViolationContext
 * Steps:
 * 1. Determine whether the field is failing. For pass/fail-capable fields the
 *    pass/fail verdict (`isPassFailFailing`) is authoritative — violation
 *    mapping triggers are NOT consulted because a desynchronised trigger
 *    condition (e.g. stored `value: true` vs `extras.failsOn: false`) would
 *    incorrectly mark a passing field as failing. For non-pass/fail fields
 *    the failing state is determined by whether any mapping trigger fires.
 *    N/A always suppresses the fail verdict.
 * 2. Union the required occurrence sub-fields across all failing mappings and
 *    the pass/fail `require*OnFail` extras.
 * 3. Attach the logged occurrences for the field.
 * Inputs: field, the form's responses, the field's logged occurrences.
 * Outputs: FieldViolationContext (failing=false when the field is not failing).
 * Idempotency: REQUIRED (pure).
 */
export function buildFieldViolationContext(
  field: InspectionFormField,
  responses: Record<string, FieldResponse>,
  occurrences: FieldOccurrenceInput[],
): FieldViolationContext {
  const fieldResponse = responses[field.id];
  const isNA = !!fieldResponse?.isNA;
  const pfFailing = isPassFailFailing(field, fieldResponse?.value, isNA);
  const failingMappings = isNA ? [] : getFailingMappings(field, responses);
  // For pass/fail fields the verdict is the single source of truth; mapping
  // triggers are only the determinant for non-pass/fail fields.
  const failing = isPassFailField(field) ? pfFailing : failingMappings.length > 0;

  const extras = getPassFailExtras(field);
  let requireLocation = !!extras.requireLocationOnFail;
  let requirePhotos = !!extras.requirePhotosOnFail;
  let requireNotes = !!extras.requireNotesOnFail;
  let requireCorrectiveAction = false;

  for (const m of failingMappings) {
    if (m.requireLocation) requireLocation = true;
    if (m.requirePhotos) requirePhotos = true;
    if (m.requireNotes) requireNotes = true;
    if (m.requireCorrectiveAction) requireCorrectiveAction = true;
  }

  return {
    failing,
    requireLocation,
    requirePhotos,
    requireNotes,
    requireCorrectiveAction,
    occurrences,
  };
}

/** Map every fieldId in a form to the section it belongs to (for hidden-section skips). */
function buildFieldSectionMap(form: InspectionForm): Record<string, string> {
  const map: Record<string, string> = {};
  for (const section of form.layout?.sections ?? []) {
    for (const fieldId of section.fieldIds ?? []) map[fieldId] = section.id;
  }
  return map;
}

/**
 * @process validateInspectionForCompletion
 * Steps:
 * 1. For each form: skip module forms (contacts/violations modules have no
 *    field-completion semantics) and skip forms resolved hidden.
 * 2. For each field: skip when its section is resolved hidden (the field-level
 *    hidden/disabled/N/A skips are handled inside getFieldIssues).
 * 3. Build the field's violation context and collect getFieldIssues(...).
 * 4. canComplete = (zero blocking issues).
 * Inputs: CompletionValidationInput. Outputs: CompletionVerdict.
 * Idempotency: REQUIRED (pure function of its inputs).
 */
export function validateInspectionForCompletion(
  input: CompletionValidationInput,
): CompletionVerdict {
  const blocking: FieldIssue[] = [];

  for (const { form, responses, resolved, occurrencesByField } of input.forms) {
    // Module forms (contacts / violations) are not field-completion gated.
    if (form.formKind === 'module') continue;
    // Whole form hidden by a rule -> nothing on it can be an issue.
    if (resolved?.hidden) continue;

    const sectionOfField = buildFieldSectionMap(form);
    const sectionHidden = resolved?.sections ?? {};

    for (const field of form.fields ?? []) {
      const sectionId = sectionOfField[field.id];
      if (sectionId && sectionHidden[sectionId]?.hidden) continue;

      const resolvedField = resolved?.fields?.[field.id];
      const occurrences = occurrencesByField?.[field.id] ?? [];
      const violation = buildFieldViolationContext(field, responses, occurrences);

      blocking.push(
        ...getFieldIssues(field, responses[field.id], resolvedField, form._id, violation),
      );
    }
  }

  return {
    canComplete: blocking.length === 0,
    blocking,
    advisory: [],
  };
}
